import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { Icon } from "../components/icons";
import * as work from "../lib/workClient";

/* The AI Copilot — ask + act. A conversational assistant grounded in the whole
 * workspace: ask who's at risk, what to do today, or how an account is doing,
 * and act on the answer — create a task, draft a note, or push a digest —
 * without leaving the chat. Live Claude when keyed; a deterministic intent
 * router otherwise, so it always answers. */

interface Msg { role: "user" | "assistant"; reply?: work.CopilotReply; text?: string; }
const SUGGESTIONS = ["Who's at risk?", "What should I do today?", "Best expansion candidates", "How is Tidewater doing?"];

function Rich({ text }: { text: string }) {
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {text.split("\n").map((line, i) => {
        const parts = line.split(/(\*[^*]+\*)/g).map((seg, j) =>
          seg.startsWith("*") && seg.endsWith("*") ? <b key={j}>{seg.slice(1, -1)}</b> : <span key={j}>{seg}</span>);
        return <div key={i} style={{ fontSize: 14, lineHeight: 1.55 }}>{parts}</div>;
      })}
    </div>
  );
}

export default function Copilot() {
  const { ws } = useWorkspace();
  const [mode, setMode] = useState<work.Mode>("local");
  const [live, setLive] = useState<boolean | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { work.aiStatus().then((r) => setLive(r.available)); work.loadChatChannels(ws).then((r) => setMode(r.mode)); }, [ws]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, thinking]);

  const ask = async (question: string) => {
    if (!question.trim() || thinking) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setInput(""); setThinking(true);
    const reply = await work.askCopilot(ws, question);
    setMsgs((m) => [...m, { role: "assistant", reply }]);
    setThinking(false);
  };
  const run = async (action: work.CopilotAction) => {
    const r = await work.actCopilot(mode, ws, action);
    setFlash(r.done); setTimeout(() => setFlash(null), 3200);
  };

  return (
    <div className="page" style={{ maxWidth: 860, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 120px)" }}>
      <div className="row between mb-24" style={{ alignItems: "flex-start" }}>
        <div>
          <p className="eyebrow">Copilot</p>
          <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>Ask &amp; act</h1>
          <p className="page__intro" style={{ marginBottom: 0 }}>Ask about your book in plain language — and act on the answer without leaving the chat.</p>
        </div>
        {live !== null && <span className={`mode-pill ${live ? "live" : ""}`}><i />{live ? "Live AI" : "Built-in AI"}</span>}
      </div>

      <div style={{ flex: 1, display: "grid", gap: 12, alignContent: "start" }}>
        {msgs.length === 0 && (
          <div className="card"><div className="card__bd" style={{ textAlign: "center", padding: "28px 20px" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--corona-soft)", color: "var(--corona-ink)", display: "grid", placeItems: "center", margin: "0 auto 12px" }}><Icon name="sparkle" size={22} /></div>
            <p style={{ fontSize: 14.5, color: "var(--muted)", marginBottom: 16 }}>Grounded in all {ws.accounts.length} accounts. Try:</p>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
              {SUGGESTIONS.map((s) => <button key={s} className="chip" onClick={() => ask(s)}>{s}</button>)}
            </div>
          </div></div>
        )}

        {msgs.map((m, i) => m.role === "user" ? (
          <div key={i} className="row" style={{ justifyContent: "flex-end" }}>
            <div style={{ background: "var(--corona-soft)", color: "var(--ink)", padding: "10px 14px", borderRadius: "14px 14px 4px 14px", maxWidth: "78%", fontSize: 14 }}>{m.text}</div>
          </div>
        ) : (
          <div key={i} className="card">
            <div className="card__bd">
              <div className="row" style={{ gap: 9, alignItems: "center", marginBottom: 10 }}>
                <span style={{ width: 24, height: 24, borderRadius: 7, background: "var(--corona-soft)", color: "var(--corona-ink)", display: "grid", placeItems: "center" }}><Icon name="sparkle" size={13} /></span>
                <b style={{ fontSize: 12.5, color: "var(--muted)" }}>Copilot{m.reply?.source === "llm" ? " · live" : ""}</b>
              </div>
              <Rich text={m.reply?.text ?? ""} />
              {!!m.reply?.refs.length && (
                <div className="row" style={{ gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                  {m.reply.refs.map((r) => <Link key={r.accountId} to={`/accounts/${r.accountId}`} className="tag" style={{ textDecoration: "none" }}>{r.label}</Link>)}
                </div>
              )}
              {!!m.reply?.actions.length && (
                <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--line-soft)" }}>
                  {m.reply.actions.map((a, j) => (
                    <button key={j} className="btn btn--ghost btn--sm" onClick={() => run(a)}>
                      <Icon name={a.kind === "create_task" ? "check" : a.kind === "add_note" ? "doc" : "bell"} size={13} /> {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {thinking && <div className="card"><div className="card__bd"><span className="faint" style={{ fontSize: 13 }}>Thinking…</span></div></div>}
        <div ref={endRef} />
      </div>

      {flash && <div className="badge badge--good" style={{ position: "sticky", bottom: 78, alignSelf: "center", margin: "0 auto" }}><Icon name="check" size={13} /> {flash}</div>}

      <form className="row" style={{ gap: 8, position: "sticky", bottom: 0, background: "var(--paper)", paddingTop: 12, paddingBottom: 4 }}
        onSubmit={(e) => { e.preventDefault(); ask(input); }}>
        <input className="input" style={{ flex: 1 }} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about your book — or tell me what to do…" />
        <button className="btn btn--primary" type="submit" disabled={!input.trim() || thinking}><Icon name="arrowRight" size={15} /></button>
      </form>
    </div>
  );
}
